import { z } from 'zod';
/**
 * The portal assistant „DAMOKLES Assistent" (Mandantenportal 22–26) and its CRM counterparts (27/28), shared by the API
 * (validation, response serialization) and the SPA (types, chips). German strings verbatim from the screens;
 * identifiers English (docs/glossary.md). Contract: docs/dev/api-portal-assistant.md.
 */
export const CHAT_TITLE = 'DAMOKLES Assistent';
export const CHAT_MESSAGE_MAX = 2000;
/** FAQ tiles of Mandantenportal 22 (a tile sends its text as a question). */
export const portalChatFaq = [
    'Welche Nebenkosten kommen auf mich zu?',
    'Was bedeutet meine Vormerkung?',
    'Kann ich die Wohnung vermieten?',
    'Wie läuft der Notartermin ab?',
];
export const chatQuickReplyKeys = [
    'financing_question',
    'appointment',
    'problem',
    'urgent',
    'normal',
];
export const chatSteps = ['idle', 'support_details', 'support_urgency'];
export const chatUrgencies = ['urgent', 'normal'];
/** Urgency chips of Mandantenportal 25. */
export const chatUrgencyLabels = {
    urgent: 'Eilig, Frist läuft',
    normal: 'Kann warten',
};
export const chatMessageBodySchema = z
    .object({
    text: z.string().trim().min(1).max(CHAT_MESSAGE_MAX).optional(),
    /** A chip: its label is recorded as the customer's message. */
    quickReply: z.enum(chatQuickReplyKeys).optional(),
})
    .refine((body) => body.text !== undefined || body.quickReply !== undefined, {
    message: 'Bitte geben Sie eine Nachricht ein.',
    path: ['text'],
});
export const chatUrgencyBodySchema = z.object({ urgency: z.enum(chatUrgencies) });
export const chatAppointmentBodySchema = z.object({
    eventTypeId: z.string().uuid(),
    startsAt: z.iso.datetime({ offset: true }),
});
export const assistantNoteBodySchema = z.object({ text: z.string().trim().min(1).max(4000) });
// --- responses ---------------------------------------------------------------------------------------
const tone = z.enum(['neutral', 'info', 'accent', 'warning', 'danger', 'success']);
export const chatSlotSchema = z.object({
    startsAt: z.string(),
    /** „Do, 18.09. · 16:00 Uhr" */
    label: z.string(),
    /** „vor Ort" | „Telefon" | „Video" */
    channelLabel: z.string(),
});
export const portalTicketCardSchema = z.object({
    id: z.string().uuid(),
    numberLabel: z.string(),
    title: z.string(),
    statusLabel: z.string(),
    tone: z.enum(['info', 'warning', 'success', 'neutral']),
    meta: z.string(),
    steps: z.array(z.object({ label: z.string(), detail: z.string().nullable(), done: z.boolean() })),
});
export const chatCardSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('slots'), eventTypeId: z.string().uuid(), slots: z.array(chatSlotSchema) }),
    z.object({
        kind: z.literal('appointment'),
        appointmentId: z.string().uuid(),
        /** „Termin angefragt" while the advisor has not confirmed; „Termin gebucht" otherwise. */
        eyebrow: z.string(),
        /** „Do, 18.09.2026 · 16:00 Uhr" */
        whenLabel: z.string(),
        /** „Mit Jonas Weiler · Treffpunkt Lindenhof 7, Eingang Hof" */
        withLine: z.string(),
        status: z.enum(['pending', 'confirmed', 'cancelled']),
        /** Reschedule/cancel page of the booking link (`/book/t/<token>`); null once the appointment is over. */
        manageUrl: z.string().nullable(),
        canCancel: z.boolean(),
    }),
    z.object({ kind: z.literal('transmitted'), title: z.string(), text: z.string() }),
    z.object({ kind: z.literal('ticket'), ticket: portalTicketCardSchema }),
    z.object({ kind: z.literal('attachment_request'), label: z.string(), hint: z.string() }),
]);
export const chatMessageViewSchema = z.object({
    id: z.string().uuid(),
    role: z.enum(['bot', 'customer']),
    text: z.string().nullable(),
    /** „11:04" today, „16.09., 11:04" before. */
    atLabel: z.string(),
    card: chatCardSchema.nullable(),
    /** A receipt the customer attached (Mandantenportal 25 „Datei hinzufügen"). */
    attachment: z.object({ name: z.string() }).nullable(),
});
export const chatViewSchema = z.object({
    header: z.object({ title: z.string(), line: z.string() }),
    faq: z.array(z.string()),
    messages: z.array(chatMessageViewSchema),
    quickReplies: z.array(z.object({ key: z.enum(chatQuickReplyKeys), label: z.string() })),
    step: z.enum(chatSteps),
    placeholder: z.string(),
});
/** CRM counterpart of a timeline item (Mandantenportal 27/28). */
export const timelinePortalPills = ['Termin', 'Notiz', 'Portal', 'Dokument', 'Supportticket'];
export const timelinePortalActions = [
    'confirm_appointment',
    'reschedule_appointment',
    'edit_note',
    'open_ticket',
];
export const timelinePortalSchema = z.object({
    pill: z.enum(timelinePortalPills),
    footnote: z.string().nullable(),
    statusPill: z.object({ label: z.string(), tone }).nullable(),
    meta: z.string().nullable(),
    actions: z.array(z.enum(timelinePortalActions)),
});
//# sourceMappingURL=portal-assistant.js.map